/* eslint-disable import/no-cycle */

import React from 'react'

import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import { changeModalState } from 'actions/modalActions'
import Button from 'components/button'
import { Title5 } from 'components/fonts'
import Icon, { IconNames } from 'components/icons'
import Input from 'components/input'
import ReCaptcha from 'components/recaptchaV2/reCaptcha'
import MODAL_NAMES from 'constants/modalNames'
import e2eClasses from 'helpers/automation'
import NAMESPACES from 'localization/constants/namespaces'

import CredentialRecoveryModal from './credentials-recovery'
import { getLoginValidationSchema } from './login-validation-schema'
import {
  Label,
  FormContainer,
  IconContainer,
  ButtonWrapper,
  StyledContainer,
  StyledLabel,
  StyledLink,
  RecoveryLinkContainer
} from './styled'

const CorporateAgreementsLogin = props => {
  const { t } = useTranslation(NAMESPACES.CORPORATE_AGREEMENTS)
  const [recoveryType, setRecoveryType] = React.useState('password')

  const getInitialValues = () => ({
    userName: '',
    password: ''
  })

  const handleSubmit = values => {
    props.handleLogin && props.handleLogin(values)
  }

  const recaptchaRef = React.createRef()

  const formik = useFormik({
    initialValues: getInitialValues(),
    onSubmit: handleSubmit,
    validationSchema: getLoginValidationSchema(t)
  })

  const toggleCredentialType = () => {
    setRecoveryType(prevType => (prevType === 'password' ? 'userEmail' : 'password'))
  }

  const showRecoveryModal = credentialType => event => {
    event.preventDefault()
    setRecoveryType(credentialType)
    props.changeModalState({
      isVisible: true,
      modalName: MODAL_NAMES.CORPORATE_CREDENTIALS_RECOVERY
    })
  }

  const hideRecoveryModal = () => {
    props.changeModalState({
      isVisible: false,
      modalName: MODAL_NAMES.CORPORATE_CREDENTIALS_RECOVERY
    })
  }

  return (
    <React.Fragment>
      <CredentialRecoveryModal
        credentialType={recoveryType}
        onClose={hideRecoveryModal}
        toggleCredentialType={toggleCredentialType}
        modalName={MODAL_NAMES.CORPORATE_CREDENTIALS_RECOVERY}
      />
      <StyledContainer>
        <StyledLabel>
          <IconContainer>
            <Icon name={IconNames.AccountBorderless} />
          </IconContainer>
          <Title5>{props.title}</Title5>
        </StyledLabel>
        <form
          onSubmit={e => {
            e.preventDefault()
            recaptchaRef.current?.execute()
          }}
        >
          <FormContainer>
            <Label e2eClass={e2eClasses('corporate.username')}>
              {t('agreements.login.username-label')}
            </Label>
            <Input
              name="userName"
              placeholder={t('agreements.login.username-placeholder')}
              smallPadding
              light
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.userName}
              error={formik.touched.userName && formik.errors.userName}
              aria-label={t('agreements.login.username-label')}
              role="textbox"
            />
            <StyledLink href="#" onClick={showRecoveryModal('userEmail')}>
              {t('agreements.login.forgot-username')}
            </StyledLink>
            <Label e2eClass={e2eClasses('corporate.password')}>
              {t('agreements.login.password-label')}
            </Label>
            <Input
              name="password"
              type="password"
              placeholder={t('agreements.login.password-placeholder')}
              smallPadding
              light
              error={t(props.error) || (formik.touched.password && formik.errors.password)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.password}
              aria-label={t('agreements.login.password-label')}
            />
            <RecoveryLinkContainer>
              <StyledLink href="#" onClick={showRecoveryModal('password')}>
                {t('agreements.login.forgot-password')}
              </StyledLink>
            </RecoveryLinkContainer>
          </FormContainer>
          <ButtonWrapper>
            <Button e2eClass={e2eClasses('corporate.loginButton')} type="submit">
              {t('label.login')}
            </Button>
          </ButtonWrapper>
          <ReCaptcha ref={recaptchaRef} onSuccess={() => handleSubmit(formik.values)} />
        </form>
      </StyledContainer>
    </React.Fragment>
  )
}

CorporateAgreementsLogin.propTypes = {
  title: PropTypes.string
}

const mapDispatchToProps = {
  changeModalState
}

export default connect(null, mapDispatchToProps)(CorporateAgreementsLogin)
